import { FunctionComponent as FC, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { loadScriptByURL } from 'vopay';
import GoogleButton from '@google-pay/button-react';
import { useToken } from '@/providers/Token.provider';
import usePostPaylinkSetAccount from '@/hooks/useApi/usePostPaylinkSetAccount';
import useGooglePay from './useGooglePay';
import useFetchiQ11AccessTokenInfo from '@/hooks/useApi/useFetchiQ11AccessTokenInfo';
import useStorage from '@/hooks/useStorage';
import { usePaylink } from '@/providers/Paylink.provider';

type GooglePayButtonProps = {
  className?: string;
};

const GooglePayButton: FC<GooglePayButtonProps> = ({ className }) => {
  const { linkGooglePay } = useGooglePay({});
  const storage = useStorage();
  const { paylink, Token } = usePaylink();
  const { iQ11Token: IframeKey, iQ11TokenInfo } = storage.token.getIQ11Token();
  const [isReadyToPay, setIsReadyToPay] = useState(false);
  const env = import.meta.env.VITE_GOOGLE_PAY_ENV as 'TEST' | 'PRODUCTION';
  const history = useHistory();
  const [accessTokenResponse, , fetchIQ11AccessTokenInfo] = useFetchiQ11AccessTokenInfo({});
  const { token } = useToken();

  const tokenizationSpecificationParams = useMemo(
    () => accessTokenResponse.ExtraInfo.TokenizationSpecification || {},
    [accessTokenResponse],
  );

  useEffect(() => {
    fetchIQ11AccessTokenInfo({
      IframeKey,
      PaymentSelectionMethod: 'googlepay',
      ForOnboarding: false,
    });
  }, []);

  useEffect(() => {
    if (!accessTokenResponse.Success) return;

    loadScriptByURL('googlepay', 'https://pay.google.com/gp/p/js/pay.js')
      .then(() => {
        const paymentsClient = new google.payments.api.PaymentsClient({
          environment: env,
        });

        return paymentsClient.isReadyToPay(createPaymentRequest());
      })
      .then(({ result }) => {
        setIsReadyToPay(result);
      });
  }, [accessTokenResponse]);

  /**
   * The [Google Pay payment request](https://developers.google.com/pay/api/web/guides/tutorial)
   */
  function createPaymentRequest(): google.payments.api.PaymentDataRequest {
    const paymentRequest: google.payments.api.PaymentDataRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['AMEX', 'DISCOVER', 'JCB', 'MASTERCARD', 'VISA'],

            billingAddressRequired: true,
            billingAddressParameters: {
              format: 'FULL',
              phoneNumberRequired: false,
            },
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: tokenizationSpecificationParams,
          },
        },
      ],
      merchantInfo: {
        // merchantId: import.meta.env.VITE_GOOGLE_PAY_MERCHANT_ID,
        // merchantId: 'BCR2DN4TUPXZJM2E',
        merchantId: import.meta.env.VITE_GOOGLE_PAY_MERCHANT_ID,
        // merchantName: 'VoPay International',
      },
      // I think there's a bug in this transactionInfo type?
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      transactionInfo: Number(paylink.Amount)
        ? {
            totalPriceStatus: 'FINAL',
            totalPriceLabel: 'Total',
            totalPrice: paylink.Amount,
            currencyCode: iQ11TokenInfo?.Currency || 'CAD',
            countryCode: iQ11TokenInfo?.Country || 'CA',
          }
        : {
            totalPriceStatus: 'NOT_CURRENTLY_KNOWN',
            currencyCode: iQ11TokenInfo?.Currency || 'CAD',
            // totalPrice: '0',
          },
      // shippingAddressRequired: true,
      // shippingOptionParameters: shippingOptionParameters,
      // shippingOptionRequired: true,
      // callbackIntents: ['SHIPPING_ADDRESS', 'SHIPPING_OPTION']
      // callbackIntents: ['PAYMENT_METHOD'],
      callbackIntents: ['PAYMENT_AUTHORIZATION'],
      emailRequired: true,
    };

    debug('env:', env);
    debug('createPaymentRequest', { paylink, paymentRequest });

    return paymentRequest;
  }

  function debug(...args: unknown[]) {
    console.log(...args);
  }

  const [, , , setPaylinkAccount] = usePostPaylinkSetAccount({});

  if (!isReadyToPay) return null;

  return (
    <GoogleButton
      environment={env}
      buttonSizeMode="fill"
      paymentRequest={createPaymentRequest()}
      // onLoadPaymentData={handleLoadPaymentData}
      onError={(error) => {
        console.error(error);
      }}
      onPaymentAuthorized={async (e) => {
        /** Loads payment data and routes to the confirmation page
         *
         * @param {google.payments.api.PaymentData} data Google Pay payment data
         */
        debug('onPaymentAuthorized:', e);

        const maskedCardNumber = (e.paymentMethodData.info?.cardDetails || '').padStart(16, 'X');
        const cardBrand = e.paymentMethodData.info?.cardNetwork || 'unknown';
        const name = e.paymentMethodData.info?.billingAddress?.name || '';
        const email = e.email || '';

        const req = {
          PaymentToken: {
            token: e.paymentMethodData.tokenizationData.token,
          },
          CardHolderName: name,
          MaskedCardNumber: maskedCardNumber,
          CardBrand: cardBrand,
          Email: email,
        };

        debug('token before parsing: ' + e.paymentMethodData.tokenizationData.token);
        debug({ req });

        const linkRes = await linkGooglePay(req);

        debug('done:', linkRes);

        if (Token) {
          const paylinkRes = await setPaylinkAccount({
            SingleUseToken: Token,
            Iq11Token: linkRes.Token,
            PaymentMethod: 'googlepay',
          });

          if (paylinkRes.Success) {
            history.push('/thank-you', {
              paymentMethodType: 'googlepay',
              paymentMethod: maskedCardNumber,
              cardNumber: maskedCardNumber,
              cardHolderName: name,
              cardBrand: cardBrand,
            });

            return {
              transactionState: 'SUCCESS',
            };
          }
        } else {
          const redirectMethod = token?.RedirectMethod;
          const url = `${token?.RedirectURL || ''}?Token=${linkRes.Token}&PaymentMethod=googlepay`;

          const Response = {
            Step: 'LINK',
            Url: url,
            Token: linkRes.Token,
            PaymentMethod: 'googlepay',
          };

          // Inner and outer redirect
          window.top?.postMessage(Response, '*');

          if (window.parent && window.parent !== window.top) {
            window.parent.postMessage(Response, '*');
          }

          await wait();

          if (redirectMethod === 'InnerRedirect') {
            window.location.href = url;
          } else if (redirectMethod === 'OuterRedirect') {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            window.top!.location.href = url;
          } else {
            history.push('/success');
          }

          return {
            transactionState: 'SUCCESS',
          };
        }

        const res: google.payments.api.PaymentAuthorizationResult = {
          transactionState: 'ERROR',
          error: {
            reason: 'OTHER_ERROR',
            intent: 'PAYMENT_AUTHORIZATION',
            message: 'Something went wrong',
          },
        };

        return res;
      }}
      className={className}
      buttonType="pay"
      // onPaymentDataChanged={paymentData => getUpdatedPaymentData(paymentRequest, paymentData)}
    />
  );
};

export default GooglePayButton;

async function wait() {
  return new Promise<void>((res) => {
    setTimeout(() => {
      res();
    }, 300);
  });
}
